import { useEffect, useRef, useState } from 'react'
import { userAPI } from '../services/api'

const SAVE_DELAY_MS = 800

function readLocal(storageKey) {
  try { return JSON.parse(localStorage.getItem(storageKey)) } catch { return null }
}

/**
 * A report's working column layout, saved to the signed-in user's account.
 *
 * It used to live only in localStorage: it never followed anyone to another
 * browser, and a power user's setup was invisible to everyone else. Now the
 * local copy paints instantly, the account copy replaces it once loaded, and
 * every change is saved back (debounced) — a drag moves several columns.
 *
 * A layout that exists only locally (everyone's, before this) is uploaded the
 * first time the report opens, so nobody loses the setup they already had.
 */
export function useReportLayout(report, normalize, storageKey) {
  const initialLocal = useRef(readLocal(storageKey))
  const [layout, setLayoutState] = useState(() => normalize(initialLocal.current))
  const [loaded, setLoaded] = useState(false)
  const changed = useRef(false)

  useEffect(() => {
    let active = true
    userAPI.getReportLayout(report)
      .then(response => {
        if (!active) return
        const saved = response.data.data.layout
        if (saved && !changed.current) setLayoutState(normalize(saved))
        else if (!saved && initialLocal.current) {
          const { order, visible } = normalize(initialLocal.current)
          userAPI.saveReportLayout(report, { order, visible }).catch(() => { /* Retried on the next change. */ })
        }
      })
      .catch(() => { /* Offline or signed out: the local copy still works. */ })
      .finally(() => { if (active) setLoaded(true) })
    return () => { active = false }
  }, [report, normalize])

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(layout)) } catch { /* Storage may be disabled. */ }
    if (!loaded || !changed.current) return
    const timer = setTimeout(() => {
      userAPI.saveReportLayout(report, { order: layout.order, visible: layout.visible }).catch(() => { /* Kept locally. */ })
    }, SAVE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [layout, loaded, report, storageKey])

  const setLayout = next => {
    changed.current = true
    setLayoutState(next)
  }
  return [layout, setLayout]
}
